import { Box3, Matrix4, Vector3 } from 'three';

import type { ModelId } from '@cognite/apm-client';
import type { CognitePointCloudModel } from '@cognite/reveal';
import type {
  ClickedNodeData,
  PointCloudAnnotationMappedAssetData,
} from '@cognite/reveal-components/ui/hooks';
import type {
  AnnotationData,
  AnnotationsBoundingVolume,
  AnnotationsBox,
  AnnotationsCylinder,
} from '@cognite/sdk';

import type {
  Model3DInput,
  RevisionToModelInput,
  ThreeDModelIdentifier,
} from './types';

const createBoxAnnotationAsBox3 = ({
  box,
  matrix,
}: {
  box: AnnotationsBox;
  matrix: Matrix4;
}): Box3 => {
  const cubeCorners = [
    new Vector3(-1, -1, -1),
    new Vector3(1, -1, -1),
    new Vector3(1, 1, -1),
    new Vector3(-1, 1, -1),
    new Vector3(-1, -1, 1),
    new Vector3(1, -1, 1),
    new Vector3(1, 1, 1),
    new Vector3(-1, 1, 1),
  ];

  // Create the boxMatrix from the given box.matrix
  const boxMatrix = new Matrix4();
  boxMatrix.fromArray(box.matrix);
  boxMatrix.transpose();

  // Calculate the actualMatrix
  const actualMatrix = matrix.clone();
  actualMatrix.multiply(boxMatrix);

  // Transform each corner of the cube using the actualMatrix
  const transformedCorners = cubeCorners.map((corner) =>
    corner.applyMatrix4(actualMatrix)
  );

  // Create a new Box3 encompassing all transformed corners
  const resultBox = new Box3().setFromPoints(transformedCorners);

  return resultBox;
};

export const createCylinderAnnotationAsBox3 = ({
  cylinder,
  matrix,
}: {
  cylinder: AnnotationsCylinder;
  matrix: Matrix4;
}) => {
  const box = new Box3().setFromPoints([
    new Vector3().fromArray(cylinder.centerA),
    new Vector3().fromArray(cylinder.centerB),
  ]);

  const boxMatrix = box.applyMatrix4(matrix);
  return boxMatrix;
};

export const getBoundingBoxFromAnnotation = (
  annotation: AnnotationData,
  model: CognitePointCloudModel
) => {
  const matrix4 = model.getCdfToDefaultModelTransformation();

  if (!matrix4) return undefined;

  if ('region' in annotation && annotation.region[0].cylinder) {
    return createCylinderAnnotationAsBox3({
      cylinder: annotation.region[0].cylinder,
      matrix: matrix4,
    });
  }

  const annotationsBox = (annotation as AnnotationsBoundingVolume).region[0]
    .box as AnnotationsBox;
  if (!annotationsBox) return undefined;
  const annotationBox3 = createBoxAnnotationAsBox3({
    box: annotationsBox,
    matrix: matrix4,
  });
  return annotationBox3;
};

export const getClickedAssetFromClickedNodeData = (
  clickedNodeData?: ClickedNodeData,
  assetSpace?: string
) => {
  const clickedAssetCadClassic =
    clickedNodeData?.assetMappingResult?.assetIds[0];
  const clickedAssetCadIdm = clickedNodeData?.fdmResult?.fdmNodes[0];
  if (
    assetSpace &&
    clickedAssetCadIdm &&
    assetSpace !== clickedAssetCadIdm?.space
  ) {
    return {
      space: undefined,
      externalId: undefined,
      assetId: undefined,
    };
  }

  const clickedAssetPointCloudClassic =
    clickedNodeData?.pointCloudAnnotationMappingResult
      ? getClassicAssetIdFromAnnotation(
          clickedNodeData.pointCloudAnnotationMappingResult
        )
      : undefined;

  const clickedAssetData = {
    ...clickedAssetCadIdm,
    assetId: clickedAssetCadClassic || clickedAssetPointCloudClassic,
  };
  return clickedAssetData;
};

function getClassicAssetIdFromAnnotation(
  pointCloudAnnotationMappingResult: PointCloudAnnotationMappedAssetData[]
): number | undefined {
  const asset = pointCloudAnnotationMappingResult.at(0)?.asset;
  if (asset === undefined) return undefined;

  if ('id' in asset) {
    return asset.id;
  }
  return undefined;
}

export const getModelIdFromExternalId = (externalId: string): ModelId => {
  // The externalId should be on the form `cog_3d_model_${modelId}`
  const modelIdStr = externalId.slice('cog_3d_model_'.length);
  if (
    !externalId.startsWith('cog_3d_model_') ||
    modelIdStr === '' ||
    isNaN(Number(modelIdStr))
  ) {
    throw new Error('Invalid externalId');
  }
  return Number(modelIdStr);
};

export const getRevisionIdFromExternalId = (externalId: string): number => {
  // The externalId should be on the form `cog_3d_revision_${revisionId}`
  const revisionIdStr = externalId.slice('cog_3d_revision_'.length);
  if (
    !externalId.startsWith('cog_3d_revision_') ||
    revisionIdStr === '' ||
    isNaN(Number(revisionIdStr))
  ) {
    throw new Error('Invalid externalId');
  }
  return Number(revisionIdStr);
};

export const mapRevisionToThreeDModelIdentifier = (
  revision: RevisionToModelInput,
  models: Model3DInput[]
): ThreeDModelIdentifier | undefined => {
  if (!revision.model3D.externalId) {
    return undefined;
  }
  let revisionId: number;
  if (revision.revisionId !== undefined) {
    revisionId = revision.revisionId;
  } else if (revision.externalId !== undefined) {
    revisionId = getRevisionIdFromExternalId(revision.externalId.toString());
  } else {
    return undefined;
  }

  return {
    revisionId,
    modelId: getModelIdFromExternalId(revision.model3D.externalId),
    name: models.find(
      (model) =>
        model.externalId === revision.model3D.externalId &&
        model.space === revision.model3D.space
    )?.name,
  };
};

export const getIsNewAssetSelected = (
  currentAssetId?: string | number,
  newAssetId?: string | number
): boolean => {
  return newAssetId !== currentAssetId && newAssetId !== undefined;
};
